"use client"

import { useState } from "react"
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Badge } from "@/components/ui/badge"
import {
  Wrench,
  Clock,
  ChevronDown,
  ChevronRight,
  Settings,
  Zap,
} from "lucide-react"
import type { TranscriptMessage } from "@/types/calls"

interface ToolCall {
  id: string
  name: string
  params: Record<string, unknown>
  result?: unknown
  timeInCall?: number
  isBuiltIn: boolean
}

interface ToolCallsDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  transcript: TranscriptMessage[]
  isSuperUser: boolean
}

// Built-in ElevenLabs tools (system tools)
const BUILT_IN_TOOLS = new Set([
  "transfer_to_number",
  "end_call",
  "send_sms",
  "get_current_time",
])

// Extract tool calls from transcript
function extractToolCalls(transcript: TranscriptMessage[]): ToolCall[] {
  const toolCalls: ToolCall[] = []
  let index = 0

  // First, collect all tool_results by request_id for matching
  const resultsMap = new Map<string, { result: unknown; timeInCall: number }>()
  for (const message of transcript) {
    if (message.tool_results && Array.isArray(message.tool_results)) {
      for (const tr of message.tool_results) {
        if (tr && typeof tr === "object") {
          const trObj = tr as Record<string, unknown>
          const requestId = trObj.request_id as string
          if (requestId) {
            // Parse result_value if it's a JSON string
            let resultValue = trObj.result_value || trObj.result || trObj.output
            if (typeof resultValue === "string") {
              try {
                resultValue = JSON.parse(resultValue)
              } catch {
                // Keep as string if not valid JSON
              }
            }
            resultsMap.set(requestId, {
              result: resultValue,
              timeInCall: message.time_in_call_secs,
            })
          }
        }
      }
    }
  }

  // Now extract tool_calls and match with results
  for (const message of transcript) {
    if (message.tool_calls && Array.isArray(message.tool_calls)) {
      for (const toolCall of message.tool_calls) {
        if (toolCall && typeof toolCall === "object") {
          const tc = toolCall as Record<string, unknown>

          const name = (tc.tool_name as string) || (tc.name as string) || "unknown"
          const requestId = tc.request_id as string

          // Parse params_as_json if it's a JSON string
          let params: Record<string, unknown> = {}
          const paramsJson = tc.params_as_json as string
          if (paramsJson) {
            try {
              params = JSON.parse(paramsJson)
            } catch {
              params = { raw: paramsJson }
            }
          } else if (tc.params && typeof tc.params === "object") {
            params = tc.params as Record<string, unknown>
          }

          // Find matching result by request_id
          const matchedResult = requestId ? resultsMap.get(requestId) : undefined
          const result = matchedResult?.result

          toolCalls.push({
            id: `tool-${index++}`,
            name,
            params,
            result,
            timeInCall: message.time_in_call_secs,
            isBuiltIn: BUILT_IN_TOOLS.has(name),
          })
        }
      }
    }
  }

  return toolCalls
}

// Format tool name for display
function formatToolName(name: string): string {
  const toolNames: Record<string, string> = {
    // Built-in ElevenLabs tools
    transfer_to_number: "Call Transfer",
    end_call: "End Call",
    send_sms: "Send SMS",
    get_current_time: "Get Time",
    // Custom tools
    get_shopify_order: "Shopify Order Lookup",
    human_handover: "Escalate to Human",
    email_forward: "Email Forward",
  }

  if (toolNames[name]) {
    return toolNames[name]
  }

  // Fallback: format snake_case to Title Case
  return name
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ")
}

function ToolCallItem({
  tool,
  isSuperUser,
}: {
  tool: ToolCall
  isSuperUser: boolean
}) {
  const [expanded, setExpanded] = useState(false)

  const formatTimeInCall = (seconds: number): string => {
    const mins = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  const hasParams = tool.params && Object.keys(tool.params).length > 0
  const hasResult = tool.result !== undefined && tool.result !== null

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <div
        className="flex flex-wrap items-center justify-between gap-2 p-3 bg-gray-50 cursor-pointer hover:bg-gray-100 sm:flex-nowrap"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex min-w-0 flex-wrap items-center gap-2 sm:flex-nowrap">
          {expanded ? (
            <ChevronDown className="w-4 h-4 text-gray-500" />
          ) : (
            <ChevronRight className="w-4 h-4 text-gray-500" />
          )}
          {tool.isBuiltIn ? (
            <Settings className="w-4 h-4 text-purple-600" />
          ) : (
            <Wrench className="w-4 h-4 text-blue-600" />
          )}
          <span className="font-medium text-sm">{formatToolName(tool.name)}</span>
          {isSuperUser && (
            <code className="text-xs text-gray-500 bg-gray-200 px-1 rounded">{tool.name}</code>
          )}
        </div>
        <div className="flex items-center gap-2">
          {tool.timeInCall !== undefined && (
            <div className="flex items-center gap-1 text-xs text-gray-500">
              <Clock className="w-3 h-3" />
              {formatTimeInCall(tool.timeInCall)}
            </div>
          )}
          {tool.isBuiltIn && (
            <Badge variant="outline" className="text-purple-600 border-purple-300">
              {isSuperUser ? "ElevenLabs" : "System"}
            </Badge>
          )}
        </div>
      </div>

      {expanded && (
        <div className="p-3 border-t border-gray-200 space-y-3">
          {hasParams ? (
            <div>
              <div className="text-xs font-medium text-gray-500 mb-1">Input Parameters</div>
              <pre className="text-xs bg-gray-50 p-2 rounded overflow-x-auto whitespace-pre-wrap [overflow-wrap:anywhere] sm:whitespace-pre sm:[overflow-wrap:normal]">
                {JSON.stringify(tool.params, null, 2)}
              </pre>
            </div>
          ) : (
            <div className="text-xs text-gray-400 italic">No input parameters</div>
          )}

          {hasResult ? (
            <div>
              <div className="text-xs font-medium text-gray-500 mb-1">Result</div>
              <pre className="text-xs bg-gray-50 p-2 rounded overflow-x-auto max-h-48 overflow-y-auto whitespace-pre-wrap [overflow-wrap:anywhere] sm:whitespace-pre sm:[overflow-wrap:normal]">
                {typeof tool.result === "string" ? tool.result : JSON.stringify(tool.result, null, 2)}
              </pre>
            </div>
          ) : (
            <div className="text-xs text-gray-400 italic">No result data</div>
          )}
        </div>
      )}
    </div>
  )
}

export function ToolCallsDialog({
  open,
  onOpenChange,
  transcript,
  isSuperUser,
}: ToolCallsDialogProps) {
  // Extract tool calls from transcript
  const toolCalls = extractToolCalls(transcript)

  // Debug: log transcript data when dialog opens
  if (open && transcript.length > 0) {
    const messagesWithTools = transcript.filter(
      (m) => (m.tool_calls && m.tool_calls.length > 0) || (m.tool_results && m.tool_results.length > 0)
    )
    if (messagesWithTools.length > 0) {
      console.log("[ToolCallsDialog] Messages with tools:", messagesWithTools)
      console.log("[ToolCallsDialog] Extracted tool calls:", toolCalls)
    }
  }

  // Sort by time in call
  toolCalls.sort((a, b) => {
    if (a.timeInCall !== undefined && b.timeInCall !== undefined) {
      return a.timeInCall - b.timeInCall
    }
    return 0
  })

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="min-w-0 max-w-2xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Zap className="w-5 h-5" />
            <span>Tool Usage (</span>{toolCalls.length} <span>{toolCalls.length === 1 ? "call" : "calls"}</span><span>)</span>
          </DialogTitle>
          <DialogDescription className="sr-only">Expand a tool to inspect its input parameters and result.</DialogDescription>
        </DialogHeader>

        <ScrollArea className="min-w-0 h-[50vh] pr-4 [&_[data-radix-scroll-area-viewport]>div]:!block">
          {toolCalls.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No tools were used during this call.
            </div>
          ) : (
            <div className="space-y-2">
              {toolCalls.map((tool) => (
                <ToolCallItem
                  key={tool.id}
                  tool={tool}
                  isSuperUser={isSuperUser}
                />
              ))}
            </div>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  )
}
